import type { CellClassParams, CellStyle, IRowNode } from "ag-grid-enterprise";
import { DateUtils } from "utils/dateutils";
import type {
  ConditionalFormattingActions,
  ConditionalFormattingCondition,
  ConditionalFormattingRule
} from "./types";

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === "";

const toText = (value: unknown) =>
  isBlank(value) ? "" : String(value).trim().toLowerCase();

const toNumber = (value: unknown): number | null => {
  if (isBlank(value)) return null;
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : null;
};

const toDate = (value: unknown) => {
  if (isBlank(value)) return null;
  const date = DateUtils.dateTimeObj(value as string);
  return date?.isValid?.() ? date.startOf("day") : null;
};

const evaluateNumber = (
  value: unknown,
  condition: ConditionalFormattingCondition
) => {
  const cellValue = toNumber(value);
  const from = toNumber(condition.filter);
  const to = toNumber(condition.filterTo);

  switch (condition.type) {
    case "blank":
      return cellValue === null;
    case "notBlank":
      return cellValue !== null;
    case "equals":
      return cellValue !== null && from !== null && cellValue === from;
    case "notEqual":
      return cellValue !== null && from !== null && cellValue !== from;
    case "greaterThan":
      return cellValue !== null && from !== null && cellValue > from;
    case "lessThan":
      return cellValue !== null && from !== null && cellValue < from;
    case "inRange":
      return (
        cellValue !== null &&
        from !== null &&
        to !== null &&
        cellValue >= from &&
        cellValue <= to
      );
    case "notInRange":
      return (
        cellValue !== null &&
        from !== null &&
        to !== null &&
        (cellValue < from || cellValue > to)
      );
    default:
      return false;
  }
};

const evaluateText = (
  value: unknown,
  condition: ConditionalFormattingCondition
) => {
  const cellValue = toText(value);
  const filter = toText(condition.filter);

  switch (condition.type) {
    case "blank":
      return isBlank(value) || !cellValue;
    case "notBlank":
      return !isBlank(value) && !!cellValue;
    case "equals":
      return cellValue === filter;
    case "notEqual":
      return cellValue !== filter;
    case "contains":
      return cellValue.includes(filter);
    case "notContains":
      return !cellValue.includes(filter);
    default:
      return false;
  }
};

const evaluateDate = (
  value: unknown,
  condition: ConditionalFormattingCondition
) => {
  const cellValue = toDate(value);
  const from = toDate(condition.dateFrom ?? condition.filter);
  const to = toDate(condition.dateTo ?? condition.filterTo);

  switch (condition.type) {
    case "blank":
      return cellValue === null;
    case "notBlank":
      return cellValue !== null;
    case "equals":
      return !!cellValue && !!from && cellValue.isSame(from, "day");
    case "notEqual":
      return !!cellValue && !!from && !cellValue.isSame(from, "day");
    case "lessThan":
      return !!cellValue && !!from && cellValue.isBefore(from, "day");
    case "greaterThan":
      return !!cellValue && !!from && cellValue.isAfter(from, "day");
    case "inRange":
      return (
        !!cellValue &&
        !!from &&
        !!to &&
        !cellValue.isBefore(from, "day") &&
        !cellValue.isAfter(to, "day")
      );
    default:
      return false;
  }
};

const evaluateSet = (
  value: unknown,
  condition: ConditionalFormattingCondition
) => {
  const cellValue = toText(value);
  const values = (condition.values ?? []).map(toText);

  switch (condition.type) {
    case "blank":
      return isBlank(value) || !cellValue;
    case "notBlank":
      return !isBlank(value) && !!cellValue;
    case "equals":
      return values.includes(cellValue);
    case "notEqual":
      return !!values.length && !values.includes(cellValue);
    default:
      return false;
  }
};

export const evaluateFormattingCondition = (
  value: unknown,
  condition: ConditionalFormattingCondition
) => {
  switch (condition.filterType) {
    case "number":
      return evaluateNumber(value, condition);
    case "date":
      return evaluateDate(value, condition);
    case "set":
      return evaluateSet(value, condition);
    case "text":
      return evaluateText(value, condition);
    default:
      return false;
  }
};

export const formattingActionsToCellStyle = (
  actions: ConditionalFormattingActions
): CellStyle | undefined => {
  const decorations = [
    actions.underline ? "underline" : "",
    actions.strike ? "line-through" : ""
  ].filter(Boolean);
  const style: CellStyle = {};

  if (actions.backgroundColor) style.backgroundColor = actions.backgroundColor;
  if (actions.textColor) style.color = actions.textColor;
  if (actions.fontSize) style.fontSize = `${actions.fontSize}px`;
  if (actions.bold) style.fontWeight = "bold";
  if (actions.italic) style.fontStyle = "italic";
  if (decorations.length) style.textDecoration = decorations.join(" ");

  return Object.keys(style).length ? style : undefined;
};

/**
 * Row-group headers and group footers have no value of their own in data
 * columns, so blank / "is not" rules would wrongly match them. Tree-data
 * parents are `group` too but carry `data`, so they stay formattable.
 */
export const isConditionalFormattingExcludedRow = (
  node?: Partial<Pick<IRowNode, "group" | "footer" | "data">> | null
) => !!node && ((!!node.group && !node.data) || !!node.footer);

export const getMatchingConditionalFormattingActions = (props: {
  columnId?: string | null;
  value: unknown;
  rules: ConditionalFormattingRule[];
  node?: IRowNode | null;
}): ConditionalFormattingActions | undefined => {
  const { columnId, value, rules, node } = props;
  if (!columnId) return undefined;
  if (isConditionalFormattingExcludedRow(node)) return undefined;

  const columnFormatting = rules.find((rule) => rule.columnId === columnId);
  if (!columnFormatting) return undefined;

  for (const conditionGroup of columnFormatting.conditions) {
    if (!conditionGroup.conditions.length) continue;
    const matches = conditionGroup.conditions.some((condition) =>
      evaluateFormattingCondition(value, condition)
    );
    if (matches) return conditionGroup.actions;
  }

  return columnFormatting.defaultActions;
};

export const getConditionalFormattingCellStyle = (
  params: CellClassParams,
  rules: ConditionalFormattingRule[]
): CellStyle | undefined => {
  const columnId = params.colDef.colId || params.colDef.field;
  const matchedActions = getMatchingConditionalFormattingActions({
    columnId,
    value: params.value,
    rules,
    node: params.node
  });

  return matchedActions
    ? formattingActionsToCellStyle(matchedActions)
    : undefined;
};
